/**
 * División político-administrativa de Ecuador (provincias, cantones, parroquias).
 * Fuente: INEC — dataset público vía estructura provincias/cantones/parroquias.
 */
import rawData from './ecuadorProvincias.json';

/** Parentesco para contactos de emergencia */
export const PARENTESCO_EMERGENCIA = [
  '',
  'Padre',
  'Madre',
  'Hijo',
  'Hija',
  'Cónyuge',
  'Conviviente',
  'Hermano',
  'Hermana',
  'Abuelo',
  'Abuela',
  'Tío',
  'Tía',
  'Primo',
  'Prima',
  'Sobrino',
  'Sobrina',
  'Nuera',
  'Yerno',
  'Tutor legal',
  'Amigo',
  'Amiga',
  'Otro',
];

function normalizeKey(str) {
  return (str || '')
    .toString()
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Convierte "CUENCA (CAB. EN ...)" → título legible */
export function formatUbicacionNombre(str) {
  if (!str) return '';
  const lower = str.toLowerCase();
  return lower.replace(/(?:^|\s|[(/])\S/g, (m) => m.toUpperCase());
}

const provinciasList = Object.values(rawData).map((p) => ({
  key: normalizeKey(p.provincia),
  nombre: formatUbicacionNombre(p.provincia),
  raw: p,
}));

const byProvinciaNorm = new Map(provinciasList.map((p) => [p.key, p]));

function findProvincia(provinciaNombre) {
  if (!provinciaNombre) return null;
  const norm = normalizeKey(provinciaNombre);
  let hit = byProvinciaNorm.get(norm);
  if (hit) return hit;
  hit = provinciasList.find(
    (p) => p.nombre === provinciaNombre || normalizeKey(p.nombre) === norm
  );
  return hit || null;
}

function findCanton(provinciaEntry, cantonNombre) {
  if (!provinciaEntry?.raw?.cantones || !cantonNombre) return null;
  const norm = normalizeKey(cantonNombre);
  const cantones = Object.values(provinciaEntry.raw.cantones);
  let hit = cantones.find((c) => normalizeKey(c.canton) === norm);
  if (hit) return hit;
  hit = cantones.find((c) => formatUbicacionNombre(c.canton) === cantonNombre);
  return hit || null;
}

/** Todas las provincias (orden alfabético). */
export function getProvinciasEcuador() {
  return provinciasList.map((p) => p.nombre).sort((a, b) => a.localeCompare(b, 'es'));
}

/** Cantones de una provincia. */
export function getCantonesPorProvincia(provinciaNombre) {
  const prov = findProvincia(provinciaNombre);
  if (!prov?.raw?.cantones) return [];
  return Object.values(prov.raw.cantones)
    .map((c) => formatUbicacionNombre(c.canton))
    .sort((a, b) => a.localeCompare(b, 'es'));
}

/** Parroquias / localidades del cantón (para select de ciudad y parroquia). */
export function getCiudadesPorCanton(provinciaNombre, cantonNombre) {
  const prov = findProvincia(provinciaNombre);
  const cant = findCanton(prov, cantonNombre);
  if (!cant?.parroquias) return [];
  const uniq = new Set(
    Object.values(cant.parroquias).map((nombre) => formatUbicacionNombre(nombre))
  );
  return [...uniq].sort((a, b) => a.localeCompare(b, 'es'));
}

export const getParroquiasPorCanton = getCiudadesPorCanton;
